import { track } from '../analytics/tracker'
import { getAnalyticsContext } from '../analytics/currentContext'
import { markRequisiteCopied } from '../analytics/visitOutcome'
import { CopiedField } from '../analytics/events'

// The copied value is never reported — only which field it was and whether the copy worked.
// The outcome matters because the copy can fail silently: see the iframe note below.
const report = (field: CopiedField | undefined, ok: boolean): boolean => {
  if (!field) return ok
  try {
    if (ok && field === 'requisite') markRequisiteCopied()
    const context = getAnalyticsContext()
    if (context) {
      track({ name: 'requisite.copied', payload: { ...context, field, ok } })
    }
  } catch {
    // analytics must never break UX
  }
  return ok
}

// CPAYMENT-1672: inside a cross-origin iframe the Clipboard API is gated behind the merchant
// putting allow="clipboard-write" on the <iframe> tag. Without a fallback the copy button dies
// silently — no error, no "copied" state — and the client has no way to take the requisite.
export const copyToClipboard = async (
  value: string,
  field?: CopiedField
): Promise<boolean> => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value)
      return report(field, true)
    }
  } catch {
    // fall through to the legacy path below
  }

  const textarea = document.createElement('textarea')
  textarea.value = value
  textarea.setAttribute('readonly', '')
  textarea.style.position = 'fixed'
  textarea.style.opacity = '0'
  textarea.style.pointerEvents = 'none'
  document.body.appendChild(textarea)

  try {
    textarea.select()
    textarea.setSelectionRange(0, value.length)
    return report(field, document.execCommand('copy'))
  } catch {
    return report(field, false)
  } finally {
    document.body.removeChild(textarea)
  }
}
